import React, { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { tokenSelectorStyles as styles } from "../../deployToken.tailwind";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeployTokenCard from "./DeployTokenCard";

const BuyedTokens = ({ selectedTokenId, setSelectedTokenId, tokenData, isTokenDeployed, editTokenId, setShowUnsavedChangesModal, setNextTokenId, hasUnsavedChanges, canvasRef }) => {
  const scrollRef = useRef(null);
  const tokenRefs = useRef([]); // To store references to all token cards

  const { tokens } = useSelector((state) => state.deployToken);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Calculate the scroll amount based on screen size
  const getScrollAmount = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) return 100;
    if (screenWidth < 1024) return 150;
    return 200;
  };

  // Scroll Left
  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -getScrollAmount(), behavior: "smooth" });
  };

  // Scroll Right
  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
  };

  // Update button states for scrolling (disable left button if at the start, disable right button if at the end)
  const updateButtonStates = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0); // Enable left button if not at the start
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth); // Enable right button if not at the end
    }
  };

  // Auto-scroll to the selected token
  useEffect(() => {
    if (selectedTokenId !== null && tokenRefs.current[selectedTokenId]) {
      const tokenElement = tokenRefs.current[selectedTokenId];
      const scrollContainer = scrollRef.current;

      // Ensure the selected token is visible
      if (tokenElement && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const tokenRect = tokenElement.getBoundingClientRect();

        // If the token is out of view, scroll it into view
        if (tokenRect.left < containerRect.left || tokenRect.right > containerRect.right) {
          const scrollOffset =
            tokenRect.left < containerRect.left
              ? tokenElement.offsetLeft - getScrollAmount() // Scroll left
              : tokenElement.offsetLeft - containerRect.width + tokenElement.offsetWidth + getScrollAmount(); // Scroll right

          scrollContainer.scrollTo({ left: scrollOffset, behavior: "smooth" });
        }
      }
    }
  }, [selectedTokenId]); // Re-run whenever the selected token changes

  useEffect(() => {
    updateButtonStates(); // Check button states on mount
    window.addEventListener("resize", updateButtonStates); //Update on resize

    return () => {
      window.removeEventListener("resize", updateButtonStates); // Cleanup on unmount
    };
  }, []);

  return (
    <div className={styles.outerContainer}>
      {/* Left Icon */}
      <button
        onClick={scrollLeft}
        className={`${styles.chevron_container} ${styles.left_chevron} ${!canScrollLeft ? styles.disabledButton : ""}`}
        disabled={!canScrollLeft}
      >
        <ChevronLeftIcon />
      </button>

      {/* Main Div */}
      <div className={styles.scrollableDiv} ref={scrollRef} onScroll={updateButtonStates} style={{ scrollbarWidth: "none" }}>
        {tokens?.map((token, index) => (
            <DeployTokenCard
              key={index}
              token={token}
              index={index}
              isTokenDeployed={isTokenDeployed}
              tokenData={tokenData}
              selectedTokenId={selectedTokenId}
              setSelectedTokenId={setSelectedTokenId}
              isEditingCurrentToken={editTokenId === index}
              ref={(el) => (tokenRefs.current[index] = el)} // Pass down ref for autoscroll
              setShowUnsavedChangesModal={setShowUnsavedChangesModal}
              setNextTokenId={setNextTokenId}
              hasUnsavedChanges={hasUnsavedChanges}
              canvasRef={(el) => canvasRef.current[token?._id] = el}
            />
        ))}
      </div>

      {/* Right Icon */}
      <button
        onClick={scrollRight}
        className={`${styles.chevron_container} ${!canScrollRight ? styles.disabledButton : ""}`}
        disabled={!canScrollRight}
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
};

export default BuyedTokens;